import capitalize from 'lodash/capitalize';

import { IPrismicSlice, SortBySelectOption } from '@/types';
import { MenuService } from '@/data';
import { buildSearchBodyFromParams, SearchService } from '@/opensearch';
import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';

import {
  getSelectedCustomerTypeFromServer,
  getSelectedStoreFromServer,
} from './server';
import { getProductsFromOpenSearchResponse } from './opensearch';

let slice: IPrismicSlice;

type SliceType = typeof slice.slice_type;

const SORT_BY_MAP = {
  default: undefined,
  'Price (desc)': { value: { field: 'price', order: 'desc' } },
  'Price (asc)': { value: { field: 'price', order: 'asc' } },
};

export type SliceItems = {
  [key in SliceType]?: Extract<IPrismicSlice, { slice_type: key }>;
};

const getPrismicSlicesItems = (body: IPrismicSlice[]) => {
  let slices: SliceItems = {};

  Object.keys(body).forEach((key: string) => {
    const slice: IPrismicSlice = body[key];

    const value: SliceItems = { [slice.slice_type]: slice };

    slices = { ...slices, ...value };
  });

  return slices;
};

const getPrismicSlicesList = (slices: IPrismicSlice[]) =>
  Promise.all(
    slices.map(async (slice: IPrismicSlice) => {
      if (!slice.items && !slice.primary) return;

      const { searchProductsByGroupId } = new MenuService();

      const { selectedStore } = await getSelectedStoreFromServer();
      const { selectedCustomerType } =
        await getSelectedCustomerTypeFromServer();

      const { search } = new SearchService(
        selectedStore?.msoStoreEntityId,
        selectedStore?.msoOrgId,
        selectedCustomerType,
        selectedStore?.strict_medical_enabled,
      );

      if (
        slice.slice_type === 'products_carousel' ||
        slice.slice_type === 'product_side_by_side'
      ) {
        try {
          const {
            limit = 20,
            type,
            sort_by,
            filter_by_brand,
            product_group_id,
            filter_by_on_sale,
          } = slice.primary;

          const sortBy = SORT_BY_MAP?.[sort_by] as SortBySelectOption;

          const sortParam = {
            sort:
              sortBy && sortBy.value !== 'default'
                ? `${sortBy.value.field}${capitalize(sortBy.value.order)}`
                : '',
          };

          const idParam: { id: string[] } = { id: [] };

          if (product_group_id) {
            const productGroup = await searchProductsByGroupId({
              id: product_group_id,
            });

            idParam.id.push(...productGroup.map(e => e.id));
          }

          const onSaleParam = {
            [PRODUCT_FILTER_ATTRIBUTES._ON_SALE]: filter_by_on_sale
              ? ['true']
              : [],
          };

          const categoryParam = {
            [PRODUCT_FILTER_ATTRIBUTES._CATEGORY]:
              type && type !== 'default' ? [type.toUpperCase()] : [],
          };

          const brandParam = {
            [PRODUCT_FILTER_ATTRIBUTES._BRAND]:
              filter_by_brand && filter_by_brand !== 'default'
                ? [filter_by_brand?.replace(' ', '_').toUpperCase()!]
                : [],
          };

          const { searchBody } = buildSearchBodyFromParams({
            searchParams: {
              ...sortParam,
              ...idParam,
              ...categoryParam,
              ...brandParam,
              ...onSaleParam,
            },
            filters: {
              isAboveThreshold: [true],
              isHideFromMenu: [false],
            },
            limit: limit ? parseInt(limit?.toString(), 10) : 20,
          });

          const result = await search({ searchBody });

          const products = getProductsFromOpenSearchResponse(result);

          return {
            ...slice,
            products,
            totalCount: 0,
            selectedStore,
            selectedCustomerType,
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);

          return { ...slice, products: [], totalCount: 0 };
        }
      }

      if (['product_list', 'product_row'].includes(slice.slice_type)) {
        const idParam: { id: string[] } = {
          id: slice?.items?.map(item => item?.product_id?.trim()) as string[],
        };

        const { searchBody } = buildSearchBodyFromParams({
          searchParams: {
            ...idParam,
          },
          filters: {
            isAboveThreshold: [true],
            isHideFromMenu: [false],
          },
        });

        const result = await search({ searchBody });

        const products = getProductsFromOpenSearchResponse(result);

        return { ...slice, products, selectedStore };
      }

      return slice;
    }),
  );

const mergeSlices = (currentSlices, defaultSlices) => {
  // Validate that currentSlices is an array
  const validCurrentSlices = Array.isArray(currentSlices) ? currentSlices : [];
  const validDefaultSlices = Array.isArray(defaultSlices) ? defaultSlices : [];

  const currentSliceTypes = new Set(
    validCurrentSlices.map(slice => slice?.slice_type),
  );

  const missingDefaultSlices = validDefaultSlices.filter(
    slice => !currentSliceTypes.has(slice?.slice_type),
  );

  // Ensure all default slices are in the map (preserve current document slices)
  return [...validCurrentSlices, ...missingDefaultSlices];
};

const mergeDocumentSlices = (
  document: {
    data: { slices?: IPrismicSlice[]; bottomSlices?: IPrismicSlice[] };
  },
  defaultDocument: {
    data: { slices?: IPrismicSlice[]; bottomSlices?: IPrismicSlice[] };
  },
) => {
  return {
    slices: mergeSlices(document?.data?.slices, defaultDocument?.data?.slices),
    bottomSlices: mergeSlices(
      document?.data?.bottomSlices,
      defaultDocument?.data?.bottomSlices,
    ),
  };
};

export {
  getPrismicSlicesItems,
  getPrismicSlicesList,
  mergeDocumentSlices,
  mergeSlices,
  SORT_BY_MAP,
};
